import React from "react";
import { RunnerResult } from "./RunnerGameRun.tsx";
import { prettyFront } from "./runnerGame.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface RunnerGameSummaryProps {
    result: RunnerResult;
    /** Best score for these facts and this pace, after this run. */
    best: number | null;
    isNewBest: boolean;
    onPlayAgain(): void;
    /** Back to the picker, where the pace and the facts are both chosen. */
    onChangePace(): void;
}

/**
 * The end of a run: gates passed, then how the hearts were lost, then every
 * fact that cost one with its answer - a wrong gate says which number the
 * runner went through, a late row says only that no lane was picked (ADR 0124 §1).
 */
export function RunnerGameSummary({ result, best, isNewBest, onPlayAgain, onChangePace }: RunnerGameSummaryProps) {
    const t = useTranslation();

    return (
        <div className="quizSummary runnerSummary">
            <p className="quizSummaryCounts">{t("flashCards.runnerFinal")}</p>
            <p className="quizSummaryScore">{result.score}</p>
            <p className="quizSummaryScoreLabel">{t("flashCards.runnerScoreCaption")}</p>
            <p className="quizSummaryCounts">
                {format(t("flashCards.runnerCountsTemplate"), {
                    wrong: result.wrongGates,
                    late: result.noPicks,
                })}
            </p>

            {best !== null && (
                <p className={`quizSummaryBest${isNewBest ? " quizSummaryBest--new" : ""}`}>
                    {isNewBest ? t("flashCards.newBest") : format(t("flashCards.yourBestTemplate"), { n: best })}
                </p>
            )}

            {result.misses.length > 0 && (
                <div className="quizSummaryMisses">
                    <h3>{t("flashCards.worthAnotherLookHeading")}</h3>
                    <ul>
                        {result.misses.map((miss, i) => (
                            <li key={i}>
                                <span className="quizSummaryMissProblem">
                                    {prettyFront(miss.front)} = {miss.answer}
                                </span>
                                <span className="quizSummaryMissYours">
                                    {miss.picked === null
                                        ? t("flashCards.runnerNoPickLower")
                                        : format(t("flashCards.runnerYouPickedTemplate"), { n: miss.picked })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <button type="button" className="quizPlayAgain" onClick={onPlayAgain}>
                {t("flashCards.playAgain")}
            </button>
            <button type="button" className="quizChangeMode" onClick={onChangePace}>
                {t("flashCards.runnerChangePace")}
            </button>
        </div>
    );
}
